import type { GeneratedDocumentation } from '@/domain/documentation/contracts';
import { localizeDocumentationWarning } from '@/app/localization';

function value(current: unknown, unit: string): string {
  if (typeof current !== 'number') return 'Desconhecido';
  if (unit === 'ratio') return `${(current * 100).toFixed(1)}%`;
  if (unit === 'money_minor')
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
      current / 100,
    );
  return new Intl.NumberFormat('pt-BR').format(current);
}

const page = 'h-[760px] w-[1120px] overflow-hidden bg-[#090b0a] p-12 text-[#f4f1e8]';

export function DocumentationExportSurface({
  name,
  document,
  clientName,
  branding,
}: {
  name: string;
  document: GeneratedDocumentation;
  clientName?: string;
  branding: string;
}) {
  return (
    <div className="fixed left-[-12000px] top-0 w-[1120px]" aria-hidden="true">
      <div data-export-page className={`${page} flex flex-col justify-between`}>
        <div>
          <p className="text-sm uppercase tracking-[0.3em] text-[#c69032]">Documentação do funil</p>
          <h1 className="mt-8 text-5xl font-semibold">{name}</h1>
          {clientName ? (
            <p className="mt-4 text-xl text-[#b8b5ad]">Preparado para {clientName}</p>
          ) : null}
        </div>
        <div>
          <p className="max-w-3xl text-lg text-[#d5d1c8]">{document.overview.content}</p>
          <p className="mt-8 text-xs uppercase tracking-widest text-[#8e8b84]">
            {branding === 'minimal'
              ? 'Documentação'
              : branding === 'client' && clientName
                ? clientName
                : 'Dark Funnels'}
          </p>
        </div>
      </div>
      <div data-export-page className={page}>
        <h2 className="text-3xl font-semibold text-[#c69032]">Objetivo e responsabilidades</h2>
        <div className="mt-7 grid grid-cols-2 gap-10">
          <section>
            <h3 className="text-lg font-semibold">Objetivo</h3>
            <p className="mt-3 text-[#d5d1c8]">
              {document.objective.content?.objective ?? 'Não definido.'}
            </p>
            {document.objective.content?.target !== undefined ? (
              <p className="mt-2">
                Meta: {document.objective.content.target} {document.objective.content.unit}
              </p>
            ) : null}
          </section>
          <section>
            <h3 className="text-lg font-semibold">Responsabilidades</h3>
            <ul className="mt-3 space-y-2 text-[#d5d1c8]">
              {document.responsibilities.content.map((item) => (
                <li key={item.responsible}>
                  <strong>{item.responsible}:</strong> {item.stages.join(', ')}
                </li>
              ))}
            </ul>
          </section>
        </div>
        <h3 className="mt-10 text-lg font-semibold">Sistemas</h3>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[#d5d1c8]">
          {document.systems.content.map((item) => (
            <p key={item.id}>
              <strong>{item.name}</strong> — {item.role}
            </p>
          ))}
        </div>
        <h3 className="mt-10 text-lg font-semibold">KPIs</h3>
        <table className="mt-3 w-full text-left text-sm">
          <thead>
            <tr className="text-[#8e8b84]">
              <th className="pb-2">KPI</th>
              <th className="pb-2">Atual</th>
              <th className="pb-2">Meta</th>
              <th className="pb-2">Responsável</th>
            </tr>
          </thead>
          <tbody>
            {document.kpis.content.map((item) => (
              <tr key={item.id} className="border-t border-[#2a2d2a]">
                <td className="py-2">{item.label}</td>
                <td>{value(item.current.effectiveValue, item.unit)}</td>
                <td>{item.target ?? '—'}</td>
                <td>{item.owner ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div data-export-page className={page}>
        <h2 className="text-3xl font-semibold text-[#c69032]">Procedimento operacional padrão</h2>
        <p className="mt-3 text-[#b8b5ad]">{document.sop.content.purpose}</p>
        <ol className="mt-7 grid grid-cols-2 gap-x-10 gap-y-4 text-sm">
          {document.sop.content.steps.map((step) => (
            <li key={step.id} className="border-l-2 border-[#c69032] pl-4">
              <strong>
                {step.order}. {step.title}
              </strong>
              <p className="mt-1 text-[#b8b5ad]">{step.description}</p>
              {step.responsible ? (
                <p className="mt-1 text-xs">Responsável: {step.responsible}</p>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
      <div data-export-page className={page}>
        <h2 className="text-3xl font-semibold text-[#c69032]">Checklist de procedimento</h2>
        <ul className="mt-8 grid grid-cols-2 gap-4">
          {document.checklist.content.map((item) => (
            <li key={item.id} className="flex gap-3 border-b border-[#2a2d2a] pb-3">
              <span>☐</span>
              <span>
                {item.label}
                {item.required ? ' *' : ''}
              </span>
            </li>
          ))}
        </ul>
        {document.warnings.length ? (
          <section className="mt-10">
            <h3 className="text-lg font-semibold">Observações da documentação</h3>
            <ul className="mt-3 space-y-1 text-sm text-[#b8b5ad]">
              {document.warnings.slice(0, 8).map((warning, index) => (
                <li key={`${warning.code}-${index}`}>{localizeDocumentationWarning(warning)}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
